import type {ReactElement} from 'react';
import React from 'react';
import {
  CUSTOM_GROUP_UNGROUP_CONTROL_ID,
  CUSTOM_GROUP_UNGROUP_CONTROL_MARGIN,
  GROUP_UNGROUP_CONTROL_HEIGHT,
  GROUP_UNGROUP_CONTROL_WIDTH,
} from './custom-group-ungroup-control.types';
import type {GroupUngroupAction} from './custom-group-ungroup-control.types';
import {useAppSelector} from '@/hooks';
import {useFullySelectedGroupId} from '@Hooks/poster-editor/useFullySelectedGroupId';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import {degreesToRadians} from '@Utils/math.util';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import {ControlType} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control.types';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';

export function CustomGroupUngroupControl(props: CustomItemControlsProps): ReactElement | null {
  const isMobileVariant = useAppSelector((state) => {
    return state.posterEditor.isMobileVariant;
  });

  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });
  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsRotating = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsRotating[props.pageHashedId];
  });

  const selectedIds = useAppSelector((state) => {
    return state.posterEditor.activeItemIds[props.pageHashedId];
  });

  const fullySelectedGroupId = useFullySelectedGroupId(props.pageHashedId);

  if (isMobileVariant) {
    return null;
  }

  if (arePosterItemsMoving || arePosterItemsRotating || arePosterItemsScaling) {
    return null;
  }

  const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
  if (!page) {
    return null;
  }

  const getGroupUngroupAction = (): GroupUngroupAction | null => {
    if (!selectedIds || selectedIds.length < 2) {
      return null;
    }

    if (fullySelectedGroupId) {
      return {
        text: window.i18next.t('pmwjs_ungroup'),
        icon: 'icon-ungroup',
        onClick: (): void => {
          page.items.groups.ungroupSelection();
        },
      };
    }

    if (page.items.groups.isCurrentSelectionGroupable()) {
      return {
        text: window.i18next.t('pmwjs_group'),
        icon: 'icon-group-item',
        onClick: (): void => {
          page.items.groups.groupSelection();
        },
      };
    }

    return null;
  };

  const getControlPosition = (): CustomItemControlPositions => {
    const itemHeight = props.positionProperties.scaledHeight * props.positionProperties.posterScale;
    const itemWidth = props.positionProperties.scaledWidth * props.positionProperties.posterScale;

    const leftInitial = props.positionProperties.left * props.positionProperties.posterScale;
    const topInitial = props.positionProperties.top * props.positionProperties.posterScale;

    const angleRad = degreesToRadians(props.positionProperties.angle);
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const centerX = leftInitial + (itemWidth / 2) * cos - (itemHeight / 2) * sin;
    const centerY = topInitial + (itemWidth / 2) * sin + (itemHeight / 2) * cos;

    const pillHalfExtentOnLocalDown = Math.abs(sin) * (GROUP_UNGROUP_CONTROL_WIDTH / 2) + Math.abs(cos) * (GROUP_UNGROUP_CONTROL_HEIGHT / 2);
    const distanceFromCenter = itemHeight / 2 + CUSTOM_GROUP_UNGROUP_CONTROL_MARGIN + pillHalfExtentOnLocalDown;

    const offsetX = -sin * distanceFromCenter;
    const offsetY = cos * distanceFromCenter;

    const left = centerX + offsetX - Math.floor(GROUP_UNGROUP_CONTROL_WIDTH / 2);
    const top = centerY + offsetY - Math.floor(GROUP_UNGROUP_CONTROL_HEIGHT / 2);

    return {left, top, angle: 0};
  };

  const action = getGroupUngroupAction();
  if (!action) {
    return null;
  }

  const position = getControlPosition();

  return (
    <BaseCustomItemControl
      icon={action.icon}
      text={action.text}
      controlId={CUSTOM_GROUP_UNGROUP_CONTROL_ID}
      top={position.top}
      left={position.left}
      height={GROUP_UNGROUP_CONTROL_HEIGHT}
      angle={position.angle}
      borderRadius={'6px'}
      isVisible
      onPointerDown={action.onClick}
      controlType={ControlType.CLICK}
    />
  );
}
